/**
 * HUX-04 / D14–D15 — one-row homepage category rail.
 * Vše first · personalized middle slots · Další last (taxonomy sheet).
 * Landing “Všechny kategorie” text link is intentionally absent.
 * D14 — fit without horizontal overflow at 320/375/390/430 (prefer fit over scroll).
 */

import { useState, useSyncExternalStore } from 'react';
import { resolveLocalizedName } from 'pi-kiosk-shared';
import type { PublicDiscoveryHomeCategoryItemDTO } from '../discovery/publicDiscoveryTypes.js';

export type BrowseHomeCategoryRailProps = {
  readonly categories: readonly PublicDiscoveryHomeCategoryItemDTO[];
  readonly slotCount: 3 | 6;
  readonly activeCategoryKey: string | null;
  readonly locale: string;
  readonly allLabel: string;
  readonly moreLabel: string;
  readonly comingSoonLabel: string;
  readonly fallbackLabel: string;
  readonly onSelectAll: () => void;
  readonly onSelectCategory: (slug: string) => void;
  readonly onClearCategory: () => void;
  readonly onOpenMore: () => void;
  readonly className?: string;
};

/** Tailwind `gap-1` — keeps Vše + middle + Další inside 320 with min-w-11. */
export const HOME_CATEGORY_RAIL_GAP_PX = 4;
/** Tailwind `min-w-11` — WCAG / Spec hit ≥44. */
export const HOME_CATEGORY_RAIL_ITEM_MIN_PX = 44;
/** Vše + Další always occupy two slots. */
const HOME_CATEGORY_RAIL_FIXED_SLOTS = 2;

/**
 * Max middle category tiles that fit with Vše + Další at `min-w-11` + `gap-1`.
 * `(middle + 2) * 44 + (middle + 1) * 4 ≤ viewportWidth`
 */
export function resolveHomeCategoryRailMiddleCap(viewportWidthPx: number): number {
  const width = Math.max(0, Math.floor(viewportWidthPx));
  const fixedWidth =
    HOME_CATEGORY_RAIL_FIXED_SLOTS * HOME_CATEGORY_RAIL_ITEM_MIN_PX + HOME_CATEGORY_RAIL_GAP_PX;
  const perMiddle = HOME_CATEGORY_RAIL_ITEM_MIN_PX + HOME_CATEGORY_RAIL_GAP_PX;
  return Math.max(0, Math.floor((width - fixedWidth) / perMiddle));
}

function subscribeViewportWidth(onStoreChange: () => void): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }
  window.addEventListener('resize', onStoreChange);
  return () => {
    window.removeEventListener('resize', onStoreChange);
  };
}

function getViewportWidthSnapshot(): number {
  if (typeof window === 'undefined') {
    return 390;
  }
  return window.innerWidth;
}

function useViewportWidthPx(): number {
  return useSyncExternalStore(subscribeViewportWidth, getViewportWidthSnapshot, () => 390);
}

const RAIL_ITEM_CLASS = [
  // D14 — flex share the row; min-w-11 keeps hit≥44; max caps desktop stretch.
  'flex h-[4.5rem] min-h-11 min-w-11 max-w-[3.75rem] flex-1 basis-0 flex-col items-center justify-center gap-1',
  'overflow-hidden rounded-lg px-0.5 py-1 text-center',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  'focus-visible:outline-[var(--color-focus-ring)]',
].join(' ');

const RAIL_LABEL_BASE_CLASS =
  'line-clamp-1 w-full break-normal text-[11px] leading-tight [overflow-wrap:normal]';

function railLabelClass(active: boolean): string {
  // D15 — semibold on the visible label (not only the button wrapper).
  return [RAIL_LABEL_BASE_CLASS, active ? 'font-semibold' : 'font-medium'].join(' ');
}

function isCategoryIconUrl(icon: string): boolean {
  return /^https?:\/\//i.test(icon);
}

function categoryIconLabel(icon: string | null | undefined): string | null {
  if (icon === null || icon === undefined) {
    return null;
  }
  const trimmed = icon.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** D15 — missing icon fallback (consistent 24–28px box). */
function CategoryIconFallback(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-6 w-6 shrink-0 opacity-70"
      aria-hidden="true"
      data-testid="customer-browse-rail-icon-fallback"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="7" rx="1.25" />
      <rect x="14" y="3" width="7" height="7" rx="1.25" />
      <rect x="3" y="14" width="7" height="7" rx="1.25" />
      <rect x="14" y="14" width="7" height="7" rx="1.25" />
    </svg>
  );
}

function HomeCategoryIcon({
  icon,
}: {
  readonly icon: string | null | undefined;
}): JSX.Element {
  const label = categoryIconLabel(icon);
  const [failedUrl, setFailedUrl] = useStateSafeUrlFail();

  if (label === null || (isCategoryIconUrl(label) && failedUrl === label)) {
    return <CategoryIconFallback />;
  }
  if (isCategoryIconUrl(label)) {
    return (
      <img
        src={label}
        alt=""
        className="h-6 w-6 shrink-0 object-contain"
        aria-hidden="true"
        data-testid="customer-browse-category-icon"
        onError={() => {
          setFailedUrl(label);
        }}
      />
    );
  }
  return (
    <span
      className="flex h-6 w-6 shrink-0 items-center justify-center text-[1.375rem] leading-none"
      aria-hidden="true"
      data-testid="customer-browse-category-icon"
    >
      {label}
    </span>
  );
}

/** Local useState for icon URL fail — kept tiny to avoid extra import churn. */
function useStateSafeUrlFail(): readonly [
  string | null,
  (next: string | null) => void,
] {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  return [failedUrl, setFailedUrl];
}

/**
 * Short customer-facing label — never surface slug/tech ids (D15).
 */
export function resolveHomeCategoryRailLabel(
  category: Pick<PublicDiscoveryHomeCategoryItemDTO, 'name' | 'nameLocales'>,
  locale: string,
  fallbackLabel: string,
): string {
  const resolved = resolveLocalizedName(category.name, category.nameLocales, locale).trim();
  if (resolved.length > 0) {
    return resolved;
  }
  return fallbackLabel;
}

function railItemSurfaceClass(active: boolean, muted = false): string {
  if (muted) {
    return 'border border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-on-surface-muted)]';
  }
  if (active) {
    // D15 — non-color active: bottom bar + semibold + ring (not color-only fill).
    return [
      'border border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)]',
      'font-semibold shadow-[inset_0_-3px_0_0_var(--color-on-surface)]',
      'ring-1 ring-[var(--color-on-surface)] ring-offset-1 ring-offset-[var(--color-surface)]',
    ].join(' ');
  }
  return 'border border-[var(--color-border)] bg-[var(--color-surface-elevated)] text-[var(--color-on-surface)]';
}

export function BrowseHomeCategoryRail(props: BrowseHomeCategoryRailProps): JSX.Element {
  const allActive = props.activeCategoryKey === null || props.activeCategoryKey.trim().length === 0;
  const viewportWidthPx = useViewportWidthPx();
  const middleCap = resolveHomeCategoryRailMiddleCap(viewportWidthPx);
  const visibleMiddle = Math.min(props.slotCount, middleCap, props.categories.length);
  const visibleCategories = props.categories.slice(0, visibleMiddle);

  return (
    <div
      className={[
        // D14 — fit in viewport; overflow clipped (scroll is not Pass).
        'flex w-full min-w-0 gap-1 overflow-x-hidden overflow-y-hidden overscroll-x-contain',
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        props.className,
      ]
        .filter(Boolean)
        .join(' ')}
      data-testid="customer-browse-categories-grid"
      data-layout="rail"
      data-slot-count={String(props.slotCount)}
      data-visible-middle={String(visibleMiddle)}
      role="group"
    >
      <button
        type="button"
        className={[RAIL_ITEM_CLASS, railItemSurfaceClass(allActive)].join(' ')}
        onClick={props.onSelectAll}
        aria-pressed={allActive}
        data-testid="customer-browse-rail-vse"
        data-category-selected={allActive ? '1' : '0'}
      >
        <CategoryIconFallback />
        <span className={railLabelClass(allActive)}>{props.allLabel}</span>
      </button>

      {visibleCategories.map((category) => {
        const label = resolveHomeCategoryRailLabel(
          category,
          props.locale,
          props.fallbackLabel,
        );
        const active =
          !category.comingSoon &&
          (props.activeCategoryKey === String(category.id) ||
            props.activeCategoryKey === category.slug);

        if (category.comingSoon) {
          return (
            <div
              key={category.id}
              className={[RAIL_ITEM_CLASS, railItemSurfaceClass(false, true)].join(' ')}
              data-testid={`customer-browse-category-${category.slug}`}
              data-category-coming-soon="1"
            >
              <HomeCategoryIcon icon={category.icon} />
              <span className={railLabelClass(false)}>{label}</span>
              <span className="sr-only">{props.comingSoonLabel}</span>
            </div>
          );
        }

        return (
          <button
            key={category.id}
            type="button"
            className={[RAIL_ITEM_CLASS, railItemSurfaceClass(active)].join(' ')}
            onClick={() => {
              if (active) {
                props.onClearCategory();
                return;
              }
              props.onSelectCategory(category.slug);
            }}
            aria-pressed={active}
            data-testid={`customer-browse-category-${category.slug}`}
            data-category-selected={active ? '1' : '0'}
          >
            <HomeCategoryIcon icon={category.icon} />
            <span className={railLabelClass(active)}>{label}</span>
          </button>
        );
      })}

      <button
        type="button"
        className={[RAIL_ITEM_CLASS, railItemSurfaceClass(false)].join(' ')}
        onClick={props.onOpenMore}
        aria-haspopup="dialog"
        data-testid="customer-browse-category-dalsi"
        data-category-rail-more="1"
      >
        <svg
          viewBox="0 0 24 24"
          className="h-6 w-6 shrink-0 opacity-80"
          aria-hidden="true"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="5" cy="12" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="19" cy="12" r="1.5" fill="currentColor" stroke="none" />
        </svg>
        <span className={railLabelClass(false)}>{props.moreLabel}</span>
      </button>
    </div>
  );
}
